"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.attachDefaultConfigs = exports.addUpdatedFieldsToEntityData = exports.transformGroupedFields = exports.setPropertyValue = exports.getPropertyValue = exports.setImplicitOrder = exports.getDerivedPath = exports.isFieldEmpty = exports.orderFields = exports.applyShims = exports.getInitialFieldData = void 0;
const get_1 = __importDefault(require("lodash/get"));
const set_1 = __importDefault(require("lodash/set"));
const merge_1 = __importDefault(require("lodash/merge"));
const constants_1 = require("./constants");
const default_configs_1 = require("./fields/default-configs");
const utils_1 = require("./types/utils");
/**
 * Returns the most recent value and errors for a field
 * by looking up it's path within updatedFields; and if
 * nothing is found there it checks the fieldConfigs for
 * initial value
 *
 * @param {Object} fieldConfigs Path & initial value for field
 * @param {Object} updatedFields
 * @returns {Object}
 */
const emptyArray = []; // Passing the same array reference to prevent rerenders
function getInitialFieldData({ path, value = '', defaultValue = '', defaultSelection = '' }, entityData, allowObjects = false) {
    const defaultFieldValue = getDefaultValue({
        path,
        value,
        entityData,
        allowObjects,
        defaultValue,
        defaultSelection
    });
    value = defaultFieldValue || value;
    return { value, errors: emptyArray };
}
exports.getInitialFieldData = getInitialFieldData;
function getDefaultValue({ entityData, path, defaultValue, defaultSelection, allowObjects }) {
    const valueFromEntityData = getPropertyValue(entityData, path, allowObjects);
    if (valueFromEntityData) {
        return valueFromEntityData;
    }
    if (!isFieldEmpty(defaultValue)) {
        return defaultValue;
    }
    if (!isFieldEmpty(defaultSelection)) {
        return defaultSelection;
    }
}
/**
 * Retrieves a single property's value from entityData
 *
 * @param {Object} entityData
 * @param {String} path
 * @param {Boolean} allowObjects Determines whether an object can be retrieved
 */
function getPropertyValue(entityData, path, allowObjects = false) {
    const noValue = '';
    if (!path) {
        return noValue;
    }
    const value = (0, get_1.default)(entityData, path, noValue);
    if (isInvalidFormValueType(value)) {
        if (allowObjects && typeof value === 'object') {
            return value;
        }
        return noValue;
    }
    return value;
}
exports.getPropertyValue = getPropertyValue;
function isInvalidFormValueType(value) {
    return (typeof value !== 'string' &&
        typeof value !== 'number' &&
        typeof value !== 'boolean');
}
/**
 * Sets a value within entityData at the given path, and returns a fresh
 * copy of entityData without side effects
 *
 * @param {Object} entityData
 * @param {String} value
 * @param {String} path
 * @returns {Object}
 */
function setPropertyValue(entityData, value, path) {
    // If the path contains no period it is top level, nested set is not needed
    const updatedValue = path.includes('.')
        ? setNestedValueWithoutSideEffects(entityData, value, path)
        : (0, set_1.default)({}, path, value);
    return (0, merge_1.default)({}, entityData, updatedValue);
}
exports.setPropertyValue = setPropertyValue;
/**
 * Set the value of a nested property on an object and return a fresh copy
 * of the object.
 *
 * Example:
 *   someObject = {name: {first: 'joe'}}
 *   setNestedValueWithoutSideEffects(someObject, 'bob', 'name.first)
 *   > {name: {first: 'bob'}}
 *
 * @param {Object} entityData
 * @param {String} value
 * @param {String} path
 * @returns {Object} a copy of entityData with value at `path` changed to `value`
 */
function setNestedValueWithoutSideEffects(entityData, value, path) {
    const parentPath = path.split('.').slice(0, -1).join('.');
    const oldValuesAtParentPath = getPropertyValue(entityData, parentPath, true);
    const newValueAtPath = (0, set_1.default)({}, path, value);
    const newValueAtParentPath = getPropertyValue(newValueAtPath, parentPath, true);
    const mergedParentObjects = Object.assign(Object.assign({}, oldValuesAtParentPath), newValueAtParentPath);
    return (0, set_1.default)({}, parentPath, mergedParentObjects);
}
/**
 * Sorts a list of field object in desc order using the fields.order property
 *
 * @param {Array} fields
 * @returns {Array}
 */
const orderFields = (fields) => [...fields].sort((a, b) => {
    var _a, _b;
    return ((_a = a.order) !== null && _a !== void 0 ? _a : Infinity) - ((_b = b.order) !== null && _b !== void 0 ? _b : Infinity);
});
exports.orderFields = orderFields;
/**
 * Transforms the fields array by removing any fields that
 * are grouped via the group config and replacing with
 * a single "inline" type field. "inline" type will render
 * all fields in the same row.
 *
 * Also handles ordering of field group's sub-fields.
 *
 * For example these two fields:
 *
 * [{type: 'string', path: 'path.one', group: 'test-group'},
 * {type: 'string', path: 'path.two', group: 'test-group'}]
 *
 * Would be transformed into:
 *
 * [{type: 'inline',
 *   subFields: {
 *    'path.one': {...config},
 *    'path.two': {...config} }
 * }]
 *
 * @param {Array} fields
 * @returns {Array}
 */
const transformGroupedFields = (fields) => {
    const fieldGroups = {};
    let sectionIdentifier;
    const transformedFields = fields.filter((field) => {
        const isGrouped = field.group;
        sectionIdentifier = field.sectionIdentifier;
        if (isGrouped) {
            const groupName = field.group;
            if (!field.path) {
                field = Object.assign(Object.assign({}, field), { path: getDerivedPath(field) });
            }
            const path = field.path;
            fieldGroups[groupName] = fieldGroups[groupName] || {};
            const fieldGroup = fieldGroups[groupName];
            fieldGroup[path] = field;
            const groupedField = fieldGroup[path];
            const isOrdered = Boolean(groupedField.order);
            if (isOrdered) {
                const fieldOrder = groupedField.order;
                fieldGroup[path] = Object.assign(Object.assign({}, groupedField), { order: fieldOrder });
                // the field group is ordered among all fields
                // based its lowest sub-field order
                const shouldUpdateFieldGroupOrder = !fieldGroup.order || fieldOrder < fieldGroup.order;
                if (shouldUpdateFieldGroupOrder) {
                    fieldGroup.order = fieldOrder;
                }
            }
            return false;
        }
        return true;
    });
    Object.values(fieldGroups).forEach((group) => {
        const { order } = group, subFields = __rest(group, ["order"]);
        const field = {
            type: constants_1.FIELD_TYPE_NAMES.INLINE,
            order,
            subFields,
            sectionIdentifier
        };
        transformedFields.push(field);
    });
    return transformedFields;
};
exports.transformGroupedFields = transformGroupedFields;
function getDerivedPath(field) {
    return (
    // Use underscores to set apart parent indicators
    '_parent_' +
        Object.values(field.subFields)
            .reduce((path, subField) => {
            path += subField.path;
            if (subField.subFields) {
                path += getDerivedPath(subField);
            }
            // Use dashes to set apart subfields
            path += '-';
            return path;
        }, '')
            .slice(0, -1));
}
exports.getDerivedPath = getDerivedPath;
/**
 * Adds an "order" prop to a field based on its
 * position in the config array, if an order is not
 * already provided
 *
 * @param {Array} fields
 * @return {Array}
 */
function setImplicitOrder(fields) {
    return fields.map((field, index) => {
        if (field.order) {
            return field;
        }
        return Object.assign(Object.assign({}, field), { order: index + 1 });
    });
}
exports.setImplicitOrder = setImplicitOrder;
/**
 * Determines if a field is considered "empty" based on the provided value
 *
 * @param {*} value
 */
const isFieldEmpty = (value) => {
    return (value === '' ||
        value === undefined ||
        value === null ||
        (typeof value === 'object' &&
            !(value instanceof File) &&
            !Object.keys(value).length));
};
exports.isFieldEmpty = isFieldEmpty;
//TODO - instead of adding by updated key, add by mapping fields and passing uF/eD
// should remove need to call setEntityData()
const addUpdatedFieldsToEntityData = (updatedFields, entityData) => {
    return Object.keys(updatedFields).reduce((entityData, key) => {
        const value = updatedFields[key].value;
        return setPropertyValue(entityData, value, key);
    }, entityData);
};
exports.addUpdatedFieldsToEntityData = addUpdatedFieldsToEntityData;
// using Object.keys here because it means the function can traverse
// top level config (arrays) as well as subFields (objects)
const attachDefaultConfigs = (rawConfigs) => Object.entries(rawConfigs).reduce((newConfigs, [index, rawConfig]) => {
    if ((0, utils_1.isArrayConfigNode)(rawConfig)) {
        newConfigs[index] = Object.assign(Object.assign({}, rawConfig), { subFields: attachDefaultConfigs(rawConfig.subFields) });
    }
    else if ((0, utils_1.isMultiPartConfigNode)(rawConfig)) {
        newConfigs[index] = (0, default_configs_1.attachMultiPartConfig)(rawConfig);
    }
    else if ((0, utils_1.isBaseConfigNode)(rawConfig)) {
        newConfigs[index] = (0, default_configs_1.attachBaseConfig)(rawConfig);
    }
    else {
        newConfigs[index] = rawConfig;
    }
    return newConfigs;
}, 
// if we started with an array, we want to give one back
// too. otherwise, let's give back an object with keys
Array.isArray(rawConfigs) ? [] : {});
exports.attachDefaultConfigs = attachDefaultConfigs;
/**
 * Applies shims for backwards/forwards compatibility
 *
 * @param {Array} fields
 * @returns {Array}
 */
const applyShims = (fields) => {
    return fields.map((field) => {
        // numDigits deprecated
        if (field.type === 'ssn' && field.numDigits === 4) {
            return Object.assign(Object.assign({}, field), { type: 'partialSsn' });
        }
        // support 'string' or 'text' for standard text field
        if (field.type === 'text') {
            return Object.assign(Object.assign({}, field), { type: 'string' });
        }
        return field;
    });
};
exports.applyShims = applyShims;
//# sourceMappingURL=utils.js.map